/**
 * Calendars-to-Timeline switch (E-0101 / S-0010, E-0107 / S-0002).
 *
 * Two independent doors open the calendar timeline, in a logical OR:
 *
 * 1. `window.tenant.useAvailabilityPeriods.active` — the persisted tenant feature flag, the
 *    client opt-in. Set from Settings → New features, it survives reloads and is the normal way
 *    a tenant adopts the new schedules (E-0107).
 * 2. `window.tenant.useNewCalendarTimeline` — the console toggle, set by a developer or QA with
 *    `window.tenant.useNewCalendarTimeline = true`. Deliberately NOT persisted: it is lost on any
 *    reload or navigation, which restores the legacy behaviour. It remains the QA door on tenants
 *    that have NOT opted in.
 *
 * When either is active, the SPA injects the `X-Kp-Calendar-Timeline` header on its calls to the
 * kpweb backend (REST `/api/v2/*` + GraphQL). The kpweb backend
 * (PropagatesCalendarTimelineHeader concern) relays it to kp-gateway (S-0009), which forwards it
 * to kp-events (S-0008) to branch onto the WIP calendar timeline tables.
 *
 * The header stays redundant with the backend's own resolution of the tenant flag (E-0107 /
 * S-0001): both yield the same value, and the backend relay is what covers the contexts with no
 * SPA request at all. The front-side switch is still needed on its own for display decisions that
 * never leave the browser — see `EventsTimeline.vue`, which neutralises the legacy opening and
 * closing times in calendar mode.
 *
 * Both sources are read directly from `window` on every call (no Vuex/Pinia state): their values
 * are fixed at page load, so reactivity would buy nothing. Activating the flag from the UI
 * reloads the page (E-0107 / S-0003), which is what makes this safe.
 */
import { isAvailabilityPeriodsEnabled } from '~/composables/useTenant';

export const CALENDAR_TIMELINE_HEADER = 'X-Kp-Calendar-Timeline';

/**
 * Whether the console toggle is currently set.
 *
 * Accepts the boolean `true` and the string `'true'`, a common artefact of typing the flag in the
 * console — but nothing else, so a mistyped `'false'` stays off. The tenant flag needs no such
 * tolerance: it is server-emitted and always a real boolean, hence the plain reader above.
 *
 * @returns {boolean}
 */
function isConsoleToggleSet() {
  const value = window?.tenant?.useNewCalendarTimeline;

  return value === true || value === 'true';
}

/**
 * Whether the calendar timeline is active for this page, through either door.
 *
 * @returns {boolean}
 */
export function isCalendarTimelineEnabled() {
  return isAvailabilityPeriodsEnabled() || isConsoleToggleSet();
}

/**
 * Header bag to spread into a fetch/Apollo request.
 *
 * Empty when neither door is open, so existing (legacy) requests are left byte-for-byte
 * unchanged.
 *
 * @returns {Record<string, string>}
 */
export function calendarTimelineToggleHeaders() {
  return isCalendarTimelineEnabled() ? { [CALENDAR_TIMELINE_HEADER]: 'true' } : {};
}
